/* OZARA Editorial — storefront behaviour
 * Vanilla JS, no dependencies. Every interactive piece degrades to a working
 * plain-HTML form or link when JavaScript is unavailable.
 */
(function (root) {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Pure helpers (also exported for unit tests in Node)
   * ------------------------------------------------------------------ */
  var utils = {};

  /** Build a money formatter from a sample produced by Liquid: {{ 1234567 | money }} */
  utils.createMoneyFormatter = function (sample) {
    sample = String(sample || '$12,345.67');
    var match = sample.match(/\d[\d.,'\s  ]*\d/);
    if (!match) return function (cents) { return (cents / 100).toFixed(2); };
    var num = match[0];
    var decimals = 0, decSep = '', thouSep = '';
    var dec = num.match(/(\D)(\d{2})$/);
    if (dec && num.replace(/\D/g, '') === '1234567') { decimals = 2; decSep = dec[1]; }
    if (/\D/.test(num.charAt(2))) thouSep = num.charAt(2);
    var template = sample.replace(num, '{{n}}');
    return function (cents) {
      var value = Number(cents || 0) / 100;
      var fixed = decimals ? value.toFixed(2) : String(Math.round(value));
      var parts = fixed.split('.');
      var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
      return template.replace('{{n}}', decimals ? intPart + decSep + parts[1] : intPart);
    };
  };

  /* Shipping estimates: business days ------------------------------------
   * All dates are calendar days in the store's time zone, held as UTC-midnight
   * Date objects, so weekends, month/year changes and daylight saving are exact. */
  function ymd(d) { return d.toISOString().slice(0, 10); }
  function utcDate(y, m, day) { return new Date(Date.UTC(y, m, day)); }
  function nthWeekday(y, m, weekday, n) {
    var d = utcDate(y, m, 1), diff = (weekday - d.getUTCDay() + 7) % 7;
    return utcDate(y, m, 1 + diff + (n - 1) * 7);
  }
  function lastWeekday(y, m, weekday) {
    var d = utcDate(y, m + 1, 0), diff = (d.getUTCDay() - weekday + 7) % 7;
    return utcDate(y, m, d.getUTCDate() - diff);
  }
  function observed(d) { var w = d.getUTCDay(); return w === 6 ? utcDate(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - 1) : w === 0 ? utcDate(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1) : d; }
  /** US federal holidays for a year (observed dates when they fall on a weekend), as YYYY-MM-DD. */
  utils.usFederalHolidays = function (y) {
    return [
      observed(utcDate(y, 0, 1)), nthWeekday(y, 0, 1, 3), nthWeekday(y, 1, 1, 3), lastWeekday(y, 4, 1),
      observed(utcDate(y, 5, 19)), observed(utcDate(y, 6, 4)), nthWeekday(y, 8, 1, 1), nthWeekday(y, 9, 1, 2),
      observed(utcDate(y, 10, 11)), nthWeekday(y, 10, 4, 4), observed(utcDate(y, 11, 25))
    ].map(ymd);
  };
  /** Holiday checker: US federal holidays (optional) plus extra YYYY-MM-DD dates. */
  utils.holidayChecker = function (useUS, extra) {
    var set = {}, years = {};
    String(extra || '').split(/[\s,;]+/).forEach(function (t) { if (/^\d{4}-\d{2}-\d{2}$/.test(t)) set[t] = true; });
    return function (d) {
      var y = d.getUTCFullYear();
      if (useUS && !years[y]) {
        years[y] = true;
        // a Jan 1 holiday observed on Dec 31 belongs to the next year's list
        utils.usFederalHolidays(y).concat(utils.usFederalHolidays(y + 1)).forEach(function (h) { set[h] = true; });
      }
      return !!set[ymd(d)];
    };
  };
  utils.isBusinessDay = function (d, isHoliday) {
    var w = d.getUTCDay();
    return w !== 0 && w !== 6 && !(isHoliday && isHoliday(d));
  };
  /** Add N business days (skipping weekends and holidays) to a UTC-midnight date. */
  utils.addBusinessDays = function (date, days, isHoliday) {
    var d = new Date(date.getTime()), added = 0;
    while (added < days) {
      d = new Date(d.getTime() + 864e5);
      if (utils.isBusinessDay(d, isHoliday)) added++;
    }
    return d;
  };
  /** Store-time offset in minutes from Shopify's %z ("-0400"). */
  utils.parseOffset = function (z) {
    var m = String(z || '').match(/^([+-])(\d{2}):?(\d{2})$/);
    return m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : 0;
  };
  /**
   * Business-day window counted from the order date in the store's time zone.
   * The order date is day 0 when it is a business day before the cutoff hour,
   * otherwise the next business day is. Returns {from, to, start} (UTC-midnight dates).
   */
  utils.businessWindow = function (nowMs, offsetMin, minDays, maxDays, cutoffHour, isHoliday) {
    var local = new Date(nowMs + offsetMin * 6e4);
    var start = utcDate(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate());
    if (!utils.isBusinessDay(start, isHoliday) || local.getUTCHours() >= cutoffHour) start = utils.addBusinessDays(start, 1, isHoliday);
    return { start: start, from: utils.addBusinessDays(start, minDays, isHoliday), to: utils.addBusinessDays(start, Math.max(minDays, maxDays), isHoliday) };
  };
  /**
   * Read a business-day timeframe from product text, e.g. "Ships in 2–5 business days".
   * Returns {min, max, kind: 'ship'|'delivery'|'neutral'}, {ambiguous: true} when the text
   * gives different timeframes, or null when there is none.
   */
  utils.parseShipTimeframe = function (text) {
    var t = String(text || '').replace(/[‐-―−]/g, '-').replace(/\s+/g, ' ').toLowerCase();
    var re = /(\d{1,2})(?:\s*(?:-|to)\s*(\d{1,2}))?\s*(?:business|working)\s*days?/g, m, found = [];
    while ((m = re.exec(t))) {
      var min = Number(m[1]), max = Number(m[2] || m[1]);
      if (!(max >= min) || max > 90) continue;
      var before = t.slice(Math.max(0, m.index - 70), m.index);
      var ship = before.search(/ship|dispatch|process|fulfil|made to order|produc|prepar|leaves|sent out|out the door/);
      var del = before.search(/deliver|arriv|receive|get it|in your hands/);
      var kind = ship < 0 && del < 0 ? 'neutral' : (del > ship ? 'delivery' : 'ship');
      found.push({ min: min, max: max, kind: kind });
    }
    if (!found.length) return null;
    var first = found[0];
    for (var i = 1; i < found.length; i++) {
      if (found[i].min !== first.min || found[i].max !== first.max) return { ambiguous: true };
      if (first.kind === 'neutral') first.kind = found[i].kind;
    }
    return first;
  };

  /**
   * Parse the merchant's fit table. One row per line:
   *   SIZE | min height | max height | min weight | max weight
   * Heights in inches, weights in lb, e.g. "M | 66 | 71 | 140 | 175"
   */
  utils.parseFitTable = function (text) {
    return String(text || '').split(/\n/).map(function (line) {
      var p = line.split('|').map(function (s) { return s.trim(); });
      if (p.length < 5 || !p[0]) return null;
      var nums = p.slice(1, 5).map(Number);
      if (nums.some(isNaN)) return null;
      return { size: p[0], hMin: nums[0], hMax: nums[1], wMin: nums[2], wMax: nums[3] };
    }).filter(Boolean);
  };

  /** Recommend a size. pref: -1 closer, 0 as designed, 1 oversized. Returns {size, index, exact} or null. */
  utils.recommendSize = function (rows, heightIn, weightLb, pref) {
    if (!rows || !rows.length || !(heightIn > 0) || !(weightLb > 0)) return null;
    var best = -1, bestScore = Infinity;
    rows.forEach(function (r, i) {
      var hSpan = Math.max(1, r.hMax - r.hMin), wSpan = Math.max(1, r.wMax - r.wMin);
      var hMid = (r.hMin + r.hMax) / 2, wMid = (r.wMin + r.wMax) / 2;
      var hOut = heightIn < r.hMin ? r.hMin - heightIn : heightIn > r.hMax ? heightIn - r.hMax : 0;
      var wOut = weightLb < r.wMin ? r.wMin - weightLb : weightLb > r.wMax ? weightLb - r.wMax : 0;
      // weight drives fit on tops more than height, so it is weighted higher
      var score = (hOut / hSpan) * 1 + (wOut / wSpan) * 1.6 +
        (Math.abs(heightIn - hMid) / hSpan) * 0.05 + (Math.abs(weightLb - wMid) / wSpan) * 0.08;
      if (score < bestScore) { bestScore = score; best = i; }
    });
    var exact = rows[best] && heightIn >= rows[best].hMin && heightIn <= rows[best].hMax &&
      weightLb >= rows[best].wMin && weightLb <= rows[best].wMax;
    var index = Math.min(rows.length - 1, Math.max(0, best + (pref || 0)));
    return { size: rows[index].size, index: index, exact: !!exact };
  };

  utils.isSocialVisit = function (search, referrer, ua) {
    var src = '';
    try { src = (new URLSearchParams(search || '').get('utm_source') || '').toLowerCase(); } catch (e) { /* noop */ }
    return /instagram|tiktok|ig|fb|facebook/.test(src) ||
      /instagram\.com|tiktok\.com|facebook\.com/.test(String(referrer || '')) ||
      /Instagram|FBAN|FBAV|TikTok|musical_ly|Bytedance/i.test(String(ua || ''));
  };

  utils.debounce = function (fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  };

  /* Size & fit: pure sizing helpers -------------------------------------- */
  var SF_LADDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
  var SF_ALIAS = { XXSMALL: 'XXS', XSMALL: 'XS', EXTRASMALL: 'XS', SMALL: 'S', MEDIUM: 'M', LARGE: 'L', XLARGE: 'XL', EXTRALARGE: 'XL', XXL: '2XL', XXLARGE: '2XL', XXXL: '3XL', XXXXL: '4XL' };
  utils.sizeKey = function (v) {
    var k = String(v == null ? '' : v).toUpperCase().replace(/[\s.\-_]/g, '');
    return SF_ALIAS[k] || k;
  };
  /** Sort rank of a size label: waist numbers by value, letters by the XXS–5XL ladder, else -1. */
  utils.sizeRank = function (v) {
    var t = String(v == null ? '' : v).trim();
    if (/^\d+(\.\d+)?$/.test(t)) return 1000 + Number(t);
    return SF_LADDER.indexOf(utils.sizeKey(t));
  };
  /** Height in inches from 5'10", 5' 10, 70 (inches) or 178cm. */
  utils.parseHeight = function (s) {
    var t = String(s == null ? '' : s).trim(), m = t.match(/^(\d)\s*'\s*(\d{1,2})?/);
    if (m) return Number(m[1]) * 12 + Number(m[2] || 0);
    m = t.match(/^([\d.]+)\s*cm$/i);
    if (m) return Number(m[1]) / 2.54;
    var n = parseFloat(t);
    return isNaN(n) ? null : n;
  };
  /**
   * Fit guide text → {type:'grid', weights, rows:[{h, sizes}]} (header "Height | 110 | 125 …",
   * one row per height) or {type:'ranges', rows:[{size,hMin,hMax,wMin,wMax}]} ("M | 5'7\"-5'11\" | 145-175").
   */
  utils.parseFitGuide = function (text) {
    var lines = String(text || '').split(/\r?\n/).map(function (l) {
      return l.split('|').map(function (c) { return c.trim(); });
    }).filter(function (r) { return r.length > 1 && r[0]; });
    if (!lines.length) return null;
    var head = lines[0];
    if (head.length > 2 && head.slice(1).every(function (c) { return c !== '' && !isNaN(parseFloat(c)); })) {
      var rows = lines.slice(1).map(function (r) { return { h: utils.parseHeight(r[0]), sizes: r.slice(1) }; })
        .filter(function (r) { return r.h > 0 && r.sizes.length === head.length - 1; });
      return rows.length ? { type: 'grid', weights: head.slice(1).map(Number), rows: rows } : null;
    }
    var ranges = lines.map(function (r) {
      if (r.length < 3) return null;
      var h = r[1].split(/\s*[-–]\s*/).map(utils.parseHeight), w = r[2].split(/\s*[-–]\s*/).map(parseFloat);
      if (h.length < 2 || w.length < 2 || h.some(function (x) { return !(x > 0); }) || w.some(isNaN)) return null;
      return { size: r[0], hMin: h[0], hMax: h[1], wMin: w[0], wMax: w[1] };
    }).filter(Boolean);
    return ranges.length ? { type: 'ranges', rows: ranges } : null;
  };
  function nearestIndex(list, x) {
    var best = 0;
    list.forEach(function (v, i) { if (Math.abs(v - x) < Math.abs(list[best] - x)) best = i; });
    return best;
  }
  /** Size label for a build from a parsed guide. {size, outside} – outside: beyond the guide's range. */
  utils.lookupFit = function (guide, heightIn, weightLb) {
    if (!guide || !(heightIn > 0) || !(weightLb > 0)) return null;
    if (guide.type === 'grid') {
      var hs = guide.rows.map(function (r) { return r.h; }), ws = guide.weights;
      var ri = nearestIndex(hs, heightIn), ci = nearestIndex(ws, weightLb);
      var outside = heightIn < Math.min.apply(null, hs) - 2 || heightIn > Math.max.apply(null, hs) + 2 ||
        weightLb < Math.min.apply(null, ws) - 10 || weightLb > Math.max.apply(null, ws) + 15;
      return { size: guide.rows[ri].sizes[ci], outside: outside };
    }
    var r = utils.recommendSize(guide.rows, heightIn, weightLb, 0);
    return r ? { size: r.size, outside: !r.exact } : null;
  };
  /**
   * Map a size label onto the product's own sizes and apply a step (-1 closer, +1 oversized).
   * Returns {value, exact, edge: 'small'|'large'|null}. Sizes keep the product's order unless all are rankable.
   */
  utils.matchSize = function (values, label, step) {
    if (!values || !values.length || label == null) return null;
    var ranked = values.every(function (v) { return utils.sizeRank(v) >= 0; });
    var list = ranked ? values.slice().sort(function (a, b) { return utils.sizeRank(a) - utils.sizeRank(b); }) : values.slice();
    var key = utils.sizeKey(label), idx = -1, beyond = false;
    list.forEach(function (v, i) { if (idx < 0 && utils.sizeKey(v) === key) idx = i; });
    var exact = idx >= 0;
    if (!exact) {
      beyond = ranked && (utils.sizeRank(label) < utils.sizeRank(list[0]) || utils.sizeRank(label) > utils.sizeRank(list[list.length - 1]));
      var target = utils.sizeRank(label);
      if (target < 0 || !ranked) return null;
      idx = 0;
      list.forEach(function (v, i) {
        var d = Math.abs(utils.sizeRank(v) - target), bd = Math.abs(utils.sizeRank(list[idx]) - target);
        if (d < bd || (d === bd && utils.sizeRank(v) > utils.sizeRank(list[idx]))) idx = i;
      });
    }
    var want = idx + (step || 0), clamped = Math.min(list.length - 1, Math.max(0, want));
    var edge = null;
    if (want !== clamped || beyond) edge = clamped === 0 ? 'small' : 'large';
    return { value: list[clamped], exact: exact && want === clamped, edge: edge };
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = utils;
  if (typeof window === 'undefined' || !window.customElements) return;

  /* ------------------------------------------------------------------ *
   * Browser runtime
   * ------------------------------------------------------------------ */
  var theme = window.theme = window.theme || {};
  theme.utils = utils;
  theme.formatMoney = utils.createMoneyFormatter(theme.moneySample);

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function parseHTML(html) { return new DOMParser().parseFromString(html, 'text/html'); }

  function toast(message) {
    var el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { el.hidden = true; }, 3200);
  }
  theme.toast = toast;

  /* Dialogs ------------------------------------------------------------ */
  var lastOpener = null;
  function openDialog(dialog, opener) {
    if (!dialog || dialog.open) return;
    lastOpener = opener || document.activeElement;
    dialog.showModal();
    document.body.classList.add('has-open-dialog');
    document.documentElement.classList.add('has-open-dialog');
    requestAnimationFrame(function () { dialog.classList.add('is-visible'); });
    dialog.dispatchEvent(new CustomEvent('dialog:open'));
  }
  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.classList.remove('is-visible');
    var done = function () {
      dialog.close();
      if (!$('dialog[open]')) {
        document.body.classList.remove('has-open-dialog');
        document.documentElement.classList.remove('has-open-dialog');
      }
      if (lastOpener && lastOpener.focus) lastOpener.focus({ preventScroll: true });
    };
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    reduce ? done() : setTimeout(done, 280);
  }
  theme.openDialog = openDialog;
  theme.closeDialog = closeDialog;

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-open-dialog]');
    if (opener) {
      var dlg = document.getElementById(opener.getAttribute('data-open-dialog'));
      if (dlg) { e.preventDefault(); openDialog(dlg, opener); }
      return;
    }
    var closer = e.target.closest('[data-close-dialog]');
    if (closer) { e.preventDefault(); closeDialog(closer.closest('dialog')); return; }
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('drawer')) {
      // click on backdrop area
      var r = e.target.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) closeDialog(e.target);
    }
  });
  document.addEventListener('cancel', function (e) {
    if (e.target.tagName === 'DIALOG') { e.preventDefault(); closeDialog(e.target); }
  }, true);

  /* Header ------------------------------------------------------------- */
  function initHeader() {
    var wrap = $('.site-header-wrapper');
    if (!wrap) return;
    var setH = function () { document.documentElement.style.setProperty('--header-h', wrap.offsetHeight + 'px'); };
    setH();
    if ('ResizeObserver' in window) new ResizeObserver(setH).observe(wrap);
    var onScroll = function () { wrap.classList.toggle('is-scrolled', window.scrollY > 4); };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* Dropdown menu (desktop) --------------------------------------------
   * One header link opens a panel with every menu section, lined up 20px left
   * of the link. Click toggles; on hover devices it also opens on hover with a
   * short delay. Close button, Esc, a click outside or focus leaving the header close it. */
  class MegaMenu extends HTMLElement {
    connectedCallback() {
      this.wrap = this.closest('[data-header]');
      if (!this.wrap) return;
      this.triggers = $$('[data-mega-trigger]', this.wrap);
      this.hover = window.matchMedia('(hover: hover)');
      var self = this;
      this.triggers.forEach(function (t) {
        t.addEventListener('click', function () {
          // with a mouse the hover already opened it, so a click never closes it straight away
          if (self.isOpen && !self.hover.matches) self.close(); else self.open(t);
        });
        t.addEventListener('mouseenter', function () { if (self.hover.matches) self.later(function () { self.open(t); }, self.isOpen ? 0 : 90); });
        t.addEventListener('mouseleave', function () { if (self.hover.matches) self.later(function () { self.close(); }, 250); });
      });
      this.addEventListener('mouseenter', function () { clearTimeout(self.timer); });
      this.addEventListener('mouseleave', function () { if (self.hover.matches) self.later(function () { self.close(); }, 250); });
      this.addEventListener('click', function (e) { if (e.target.closest('[data-mega-close]')) self.close(true); });
      this.wrap.addEventListener('keydown', function (e) { if (e.key === 'Escape' && self.isOpen) self.close(true); });
      this.wrap.addEventListener('focusout', function (e) { if (self.isOpen && e.relatedTarget && !self.wrap.contains(e.relatedTarget)) self.close(); });
      document.addEventListener('click', function (e) { if (self.isOpen && !self.wrap.contains(e.target)) self.close(); });
      window.addEventListener('resize', utils.debounce(function () { if (window.innerWidth < 990) self.close(); else if (self.isOpen) self.place(); }, 100));
    }
    later(fn, ms) { clearTimeout(this.timer); this.timer = setTimeout(fn, ms); }
    place() {
      var t = this.trigger || this.triggers[0];
      if (!t) return;
      var left = t.getBoundingClientRect().left - this.wrap.getBoundingClientRect().left - 20;
      this.style.left = Math.max(0, left) + 'px';
    }
    open(trigger) {
      clearTimeout(this.timer);
      this.trigger = trigger || this.triggers[0];
      this.place();
      this.isOpen = true;
      this.removeAttribute('inert');
      this.classList.add('is-open');
      var active = this.trigger;
      this.triggers.forEach(function (t) { t.setAttribute('aria-expanded', String(t === active)); });
    }
    close(returnFocus) {
      clearTimeout(this.timer);
      if (!this.isOpen) return;
      var trigger = this.trigger;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      this.setAttribute('inert', '');
      if (returnFocus && trigger) trigger.focus({ preventScroll: true });
    }
  }
  customElements.define('mega-menu', MegaMenu);

  /* Cart --------------------------------------------------------------- */
  var Cart = {
    sectionIds: function () {
      var ids = $$('[data-cart-section]').map(function (el) { return el.getAttribute('data-cart-section'); });
      if (ids.indexOf('cart-drawer') === -1) ids.push('cart-drawer');
      return ids;
    },
    request: function (url, body) {
      body.sections = Cart.sectionIds().join(',');
      body.sections_url = window.location.pathname;
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) {
            var err = new Error(data.description || data.message || theme.strings.cartError);
            err.data = data;
            throw err;
          }
          return data;
        });
      });
    },
    add: function (items) { return Cart.request(theme.routes.cartAdd + '.js', { items: items }); },
    change: function (key, quantity) { return Cart.request(theme.routes.cartChange + '.js', { id: key, quantity: quantity }); },
    update: function (payload) { return Cart.request(theme.routes.cartUpdate + '.js', payload); },
    render: function (sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (id) {
        var html = sections[id];
        if (!html) return;
        var doc = parseHTML(html);
        $$('[data-cart-section="' + id + '"]').forEach(function (target) {
          var fresh = doc.querySelector('[data-cart-section="' + id + '"]');
          if (fresh) target.innerHTML = fresh.innerHTML;
        });
        var count = doc.querySelector('[data-cart-count-value]');
        if (count) {
          var n = count.getAttribute('data-cart-count-value');
          $$('[data-cart-count]').forEach(function (el) { el.textContent = n === '0' ? '' : n; });
          $$('[data-cart-count-label]').forEach(function (el) { el.textContent = el.getAttribute('data-template').replace('[count]', n); });
        }
      });
      document.dispatchEvent(new CustomEvent('cart:rendered'));
    },
    afterAdd: function (data) {
      Cart.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      if (theme.cartType === 'page') { window.location.href = theme.routes.cart; return; }
      var drawer = document.getElementById('CartDrawer');
      if (drawer) openDialog(drawer); else toast(theme.strings.added);
    }
  };
  theme.cart = Cart;

  /* Cart line interactions (drawer + cart page share markup) */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-change]');
    if (btn) {
      var item = btn.closest('[data-line-key]');
      var input = item && $('[data-qty-input]', item);
      if (!input) return;
      e.preventDefault();
      var next = Math.max(0, Number(input.value) + Number(btn.getAttribute('data-qty-change')));
      updateLine(item, next);
      return;
    }
    var rm = e.target.closest('[data-line-remove]');
    if (rm) { e.preventDefault(); updateLine(rm.closest('[data-line-key]'), 0); return; }
    var rmCode = e.target.closest('[data-discount-remove]');
    if (rmCode) { e.preventDefault(); applyDiscounts(currentCodes().filter(function (c) { return c !== rmCode.getAttribute('data-discount-remove'); })); }
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-qty-input]');
    if (input) { updateLine(input.closest('[data-line-key]'), Math.max(0, Number(input.value) || 0)); return; }
    var swap = e.target.closest('[data-line-variant]');
    if (swap) { swapVariant(swap.closest('[data-line-key]'), swap.value); return; }
    var note = e.target.closest('[data-cart-note]');
    if (note) { Cart.update({ note: note.value }).catch(function () {}); }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    var input = $('input', form);
    var code = (input.value || '').trim();
    if (!code) return;
    var codes = currentCodes();
    if (codes.map(function (c) { return c.toLowerCase(); }).indexOf(code.toLowerCase()) === -1) codes.push(code);
    applyDiscounts(codes, code);
  });

  function currentCodes() {
    var root = $('#CartDrawer') || document;
    return $$('[data-discount-code]', root).map(function (el) { return el.getAttribute('data-discount-code'); });
  }

  function applyDiscounts(codes, justAdded) {
    var forms = $$('[data-discount-form] button');
    forms.forEach(function (b) { b.classList.add('is-loading'); });
    return Cart.update({ discount: codes.join(',') }).then(function (data) {
      Cart.render(data.sections);
      if (justAdded) {
        var applied = (data.discount_codes || []).filter(function (d) { return d.code.toLowerCase() === justAdded.toLowerCase(); })[0];
        if (applied && !applied.applicable) toast(theme.strings.discountInvalid);
      }
    }).catch(function (err) { toast(err.message); })
      .finally(function () { forms.forEach(function (b) { b.classList.remove('is-loading'); }); });
  }
  theme.applyDiscounts = applyDiscounts;

  function updateLine(item, qty) {
    if (!item) return;
    item.classList.add('is-updating');
    Cart.change(item.getAttribute('data-line-key'), qty).then(function (data) {
      Cart.render(data.sections);
    }).catch(function (err) {
      item.classList.remove('is-updating');
      toast(err.message);
    });
  }

  function swapVariant(item, newVariantId) {
    if (!item) return;
    var key = item.getAttribute('data-line-key');
    var qty = Number(($('[data-qty-input]', item) || {}).value || 1);
    var existing = 0;
    $$('[data-line-key]').forEach(function (other) {
      if (other !== item && other.getAttribute('data-variant-id') === String(newVariantId)) {
        existing += Number(($('[data-qty-input]', other) || {}).value || 0);
      }
    });
    var updates = {};
    updates[key] = 0;
    updates[newVariantId] = existing + qty;
    item.classList.add('is-updating');
    Cart.update({ updates: updates }).then(function (data) { Cart.render(data.sections); })
      .catch(function (err) { item.classList.remove('is-updating'); toast(err.message); });
  }

  /* Cart recommendations — loaded lazily after the drawer renders */
  function loadCartRecs() {
    var box = $('[data-cart-recs]');
    if (!box || box.getAttribute('data-loaded') === 'true') return;
    var pid = box.getAttribute('data-product-id');
    if (!pid) return;
    box.setAttribute('data-loaded', 'true');
    var base = theme.routes.recommendations + '?section_id=cart-recommendations&limit=8&product_id=' + pid;
    var tryIntent = function (intent) {
      return fetch(base + '&intent=' + intent).then(function (r) { return r.text(); }).then(function (html) {
        var doc = parseHTML(html);
        var inner = doc.querySelector('[data-recs-inner]');
        return inner && inner.children.length ? inner : null;
      });
    };
    tryIntent('complementary').then(function (inner) { return inner || tryIntent('related'); }).then(function (inner) {
      if (inner) {
        var target = $('[data-recs-target]', box);
        inner.removeAttribute('data-recs-inner');
        target.innerHTML = inner.outerHTML;
        box.hidden = false;
      }
    }).catch(function () {});
  }
  document.addEventListener('cart:rendered', loadCartRecs);
  document.addEventListener('dialog:open', function (e) { if (e.target.id === 'CartDrawer') loadCartRecs(); }, true);

  /* Quick add on product cards ---------------------------------------- */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-quick-add-toggle]');
    if (toggle) {
      e.preventDefault();
      var qa = toggle.closest('.quick-add');
      var open = !qa.classList.contains('is-open');
      $$('.quick-add.is-open').forEach(function (o) { if (o !== qa) { o.classList.remove('is-open'); $('[data-quick-add-toggle]', o).setAttribute('aria-expanded', 'false'); } });
      qa.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      if (open) { var first = $('.size-btn:not([disabled])', qa); if (first) first.focus({ preventScroll: true }); }
      return;
    }
    var add = e.target.closest('[data-quick-add-variant]');
    if (!add) return;
    e.preventDefault();
    if (add.disabled) return;
    add.classList.add('is-loading');
    add.setAttribute('aria-busy', 'true');
    Cart.add([{ id: Number(add.getAttribute('data-quick-add-variant')), quantity: 1 }]).then(function (data) {
      add.classList.remove('is-loading');
      add.classList.add('is-added');
      setTimeout(function () { add.classList.remove('is-added'); }, 1600);
      var qa2 = add.closest('.quick-add');
      if (qa2) qa2.classList.remove('is-open');
      Cart.afterAdd(data);
    }).catch(function (err) {
      add.classList.remove('is-loading');
      toast(err.message);
    }).finally(function () { add.removeAttribute('aria-busy'); });
  });

  /* Product form ------------------------------------------------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-atc]');
      this.error = this.querySelector('[data-form-error]');
      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
    onSubmit(e) {
      e.preventDefault();
      var idInput = this.form.querySelector('[name="id"]');
      if (!idInput.value) {
        document.dispatchEvent(new CustomEvent('product:needs-size', { detail: { sectionId: this.dataset.section } }));
        return;
      }
      if (this.button.getAttribute('aria-disabled') === 'true') return;
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');
      if (this.error) this.error.hidden = true;
      var fd = new FormData(this.form);
      var item = { id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) };
      if (fd.get('selling_plan')) item.selling_plan = fd.get('selling_plan');
      var props = {};
      fd.forEach(function (v, k) { var m = k.match(/^properties\[(.+)\]$/); if (m && v) props[m[1]] = v; });
      if (Object.keys(props).length) item.properties = props;
      var self = this;
      Cart.add([item]).then(Cart.afterAdd).catch(function (err) {
        if (self.error) { self.error.textContent = err.message; self.error.hidden = false; } else toast(err.message);
      }).finally(function () {
        self.button.classList.remove('is-loading');
        self.button.removeAttribute('aria-busy');
      });
    }
  }
  customElements.define('product-form', ProductForm);

  /* Variant picker ----------------------------------------------------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-product-section]');
      var json = this.querySelector('script[type="application/json"]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.lowStock = Number(this.dataset.lowStock || 5);
      this.addEventListener('change', this.onChange.bind(this));
      this.updateAvailability();
      var selected = this.currentVariant();
      if (selected) this.apply(selected, false);
      var self = this;
      document.addEventListener('product:needs-size', function () { self.flagMissing(); });
    }
    selectedOptions() {
      return $$('fieldset', this).map(function (fs) {
        var c = fs.querySelector('input:checked');
        return c ? c.value : null;
      });
    }
    currentVariant() {
      var opts = this.selectedOptions();
      if (opts.indexOf(null) !== -1) return null;
      return this.variants.filter(function (v) {
        return v.options.every(function (o, i) { return o === opts[i]; });
      })[0] || null;
    }
    onChange(e) {
      var fs = e.target.closest('fieldset');
      if (fs) {
        var label = fs.querySelector('[data-selected-value]');
        if (label) label.textContent = e.target.value;
      }
      var alert = this.section && this.section.querySelector('[data-size-alert]');
      if (alert) alert.hidden = true;
      this.updateAvailability();
      var v = this.currentVariant();
      this.apply(v, true);
    }
    updateAvailability() {
      var opts = this.selectedOptions();
      var variants = this.variants;
      $$('fieldset', this).forEach(function (fs, idx) {
        $$('input', fs).forEach(function (input) {
          var available = variants.some(function (v) {
            if (v.options[idx] !== input.value || !v.available) return false;
            return v.options.every(function (o, j) { return j === idx || opts[j] === null || opts[j] === o; });
          });
          input.classList.toggle('is-unavailable', !available);
          var lbl = input.nextElementSibling;
          if (lbl) {
            var sr = lbl.querySelector('.visually-hidden');
            if (sr) sr.textContent = available ? '' : ' — ' + theme.strings.soldOut;
          }
        });
      });
    }
    flagMissing() {
      var alert = this.section && this.section.querySelector('[data-size-alert]');
      var firstUnset = $$('fieldset', this).filter(function (fs) { return !fs.querySelector('input:checked'); })[0];
      if (alert) alert.hidden = false;
      if (firstUnset) {
        firstUnset.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var first = firstUnset.querySelector('input:not(.is-unavailable)') || firstUnset.querySelector('input');
        if (first) setTimeout(function () { first.focus({ preventScroll: true }); }, 350);
      }
    }
    apply(variant, pushUrl) {
      var s = this.section;
      if (!s) return;
      var idInputs = $$('[data-variant-id-input]', s);
      var atcs = $$('[data-atc]', s);
      var priceBox = $('[data-price]', s);
      var stock = $('[data-stock-note]', s);
      var sticky = $('[data-sticky-variant]', s);
      if (!variant) {
        // Every option chosen but no matching variant = unavailable; otherwise still choosing (e.g. 2nd size of a set)
        var allSelected = this.selectedOptions().every(function (o) { return o !== null; });
        idInputs.forEach(function (i) { i.value = ''; });
        atcs.forEach(function (b) {
          b.setAttribute('aria-disabled', allSelected ? 'true' : 'false');
          $('[data-atc-label]', b).textContent = allSelected ? theme.strings.unavailable : theme.strings.selectSize;
        });
        if (stock) stock.hidden = true;
        if (sticky) sticky.textContent = '';
        return;
      }
      idInputs.forEach(function (i) { i.value = variant.id; });
      atcs.forEach(function (b) {
        b.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
        $('[data-atc-label]', b).textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
      });
      if (priceBox && variant.price_html) priceBox.innerHTML = variant.price_html;
      $$('[data-sticky-price]', s).forEach(function (el) { el.innerHTML = variant.price_html; });
      if (sticky) sticky.textContent = variant.title;
      if (stock) {
        stock.hidden = false;
        stock.className = 'stock-note';
        if (!variant.available) {
          stock.classList.add('stock-note--out');
          stock.textContent = stock.dataset.textOut;
        } else if (variant.tracked && variant.qty > 0 && variant.qty <= this.lowStock) {
          stock.classList.add('stock-note--low');
          stock.textContent = stock.dataset.textLow.replace('[count]', variant.qty);
        } else {
          stock.textContent = stock.dataset.textIn;
        }
      }
      if (variant.media_id) {
        var gallery = $('product-gallery', s);
        if (gallery) gallery.showMedia(variant.media_id);
      }
      if (pushUrl && this.dataset.updateUrl === 'true') {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      s.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant }, bubbles: true }));
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* Postscript Back in Stock container: only visible while the selected variant is sold out.
   * Postscript decides what to place inside it; the theme never adds its own sign-up. */
  function syncBackInStock(section, variant) {
    var box = section && section.querySelector('[data-bis-container]');
    if (!box) return;
    box.toggleAttribute('data-variant-available', !variant || variant.available);
  }
  document.addEventListener('variant:change', function (e) {
    syncBackInStock(e.target.closest('[data-product-section]') || e.target, e.detail && e.detail.variant);
  });
  document.addEventListener('change', function (e) {
    // incomplete selection (e.g. one size chosen on a two-size set) has no variant: hide
    var picker = e.target.closest('variant-picker');
    if (picker && !picker.currentVariant()) syncBackInStock(picker.closest('[data-product-section]'), null);
  });

  /* Product gallery ---------------------------------------------------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.items = $$('[data-media-id]', this.track);
      this.dots = $$('[data-gallery-dot]', this);
      this.counter = this.querySelector('[data-gallery-count]');
      var self = this;
      if (this.track) this.track.addEventListener('scroll', utils.debounce(function () { self.syncDots(); }, 60), { passive: true });
      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { self.scrollTo(i); });
      });
    }
    isCarousel() { return this.track && this.track.scrollWidth > this.track.clientWidth + 4; }
    syncDots() {
      if (!this.track) return;
      var idx = Math.round(this.track.scrollLeft / this.track.clientWidth);
      this.dots.forEach(function (d, i) { d.setAttribute('aria-current', i === idx ? 'true' : 'false'); });
      if (this.counter) this.counter.textContent = (idx + 1) + ' / ' + this.items.length;
    }
    scrollTo(i) {
      var item = this.items[i];
      if (!item) return;
      if (this.isCarousel()) this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    }
    showMedia(mediaId) {
      var idx = this.items.findIndex(function (el) { return el.getAttribute('data-media-id') === String(mediaId); });
      if (idx < 0) return;
      if (this.isCarousel()) { this.scrollTo(idx); return; }
      // desktop grid: move variant image to the hero slot
      var item = this.items[idx];
      if (idx !== 0 && this.track.firstElementChild !== item) {
        this.track.insertBefore(item, this.track.firstElementChild);
        this.items = $$('[data-media-id]', this.track);
      }
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* Colorway swatches: fast, smooth switching with real page loads ----------
   * Every colorway opens as a normal page load, so Shopify Analytics, the Meta
   * Pixel and apps (Bundler, Postscript, reviews) see each product exactly as a
   * regular visit; nothing is swapped in place and no events are sent by the theme.
   * To keep it quick: the page is prefetched (HTML only, no scripts run) on hover,
   * touch or focus. On product pages (theme setting) the switch crossfades in
   * supporting browsers and keeps the scroll position. */
  (function () {
    var KEY = 'ozara:colorway';
    var prefetched = {};
    function warm(e) {
      var a = e.target.closest && e.target.closest('a.swatch');
      if (!a || a.hasAttribute('aria-current') || a.origin !== window.location.origin || prefetched[a.href]) return;
      prefetched[a.href] = true;
      var l = document.createElement('link');
      l.rel = 'prefetch'; l.href = a.href;
      document.head.appendChild(l);
    }
    document.addEventListener('mouseover', warm, { passive: true });
    document.addEventListener('touchstart', warm, { passive: true });
    document.addEventListener('focusin', warm);

    var smooth = document.documentElement.hasAttribute('data-colorway-smooth');
    function store(v) { try { if (v) sessionStorage.setItem(KEY, JSON.stringify(v)); else sessionStorage.removeItem(KEY); } catch (err) {} }
    function read() { try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (err) { return null; } }

    document.addEventListener('click', function (e) {
      if (!smooth || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest('.swatches--product a.swatch');
      if (!a) return;
      if (a.hasAttribute('aria-current')) { e.preventDefault(); return; }
      store({ y: window.scrollY, to: a.pathname, t: Date.now() });
    });
    // Only colorway switches crossfade; any other navigation skips the transition
    window.addEventListener('pageswap', function (e) {
      var s = read();
      if (e.viewTransition && !(s && e.activation && e.activation.entry && new URL(e.activation.entry.url).pathname === s.to)) e.viewTransition.skipTransition();
    });
    var s = read();
    if (s) {
      store(null);
      if (Date.now() - s.t < 15000 && window.location.pathname === s.to && document.querySelector('.swatches--product')) window.scrollTo(0, s.y);
    }
  })();

  /* Zoom: open large images in a dialog */
  document.addEventListener('click', function (e) {
    var z = e.target.closest('[data-zoom-index]');
    if (!z) return;
    var dlg = document.getElementById(z.getAttribute('data-zoom-target'));
    if (!dlg) return;
    openDialog(dlg, z);
    var target = dlg.querySelectorAll('[data-zoom-item]')[Number(z.getAttribute('data-zoom-index'))];
    if (target) setTimeout(function () { target.scrollIntoView({ block: 'start' }); }, 30);
  });

  /* Sticky add to cart ------------------------------------------------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      var section = this.closest('[data-product-section]');
      var anchor = section && section.querySelector('[data-atc-anchor]');
      if (!anchor || !('IntersectionObserver' in window)) return;
      var self = this;
      var footer = document.querySelector('.footer');
      var pastAnchor = false, footerVisible = false;
      var update = function () {
        var show = pastAnchor && !footerVisible && !document.body.classList.contains('has-open-dialog');
        self.classList.toggle('is-visible', show);
        self.setAttribute('aria-hidden', String(!show));
        $$('a, button, input, select', self).forEach(function (el) { el.tabIndex = show ? 0 : -1; });
      };
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { pastAnchor = !en.isIntersecting && en.boundingClientRect.top < 0; });
        update();
      }).observe(anchor);
      if (footer) new IntersectionObserver(function (entries) { footerVisible = entries[0].isIntersecting; update(); }).observe(footer);
      this.querySelector('[data-sticky-submit]').addEventListener('click', function (e) {
        e.preventDefault();
        var form = section.querySelector('product-form form');
        if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
      update();
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* Shipping estimate -------------------------------------------------- */
  class ShipEstimate extends HTMLElement {
    connectedCallback() {
      var d = this.dataset, tf = null, source = 'metafields';
      var min = parseInt(d.min, 10), max = parseInt(d.max, 10);
      if (!isNaN(min) || !isNaN(max)) {
        if (isNaN(min)) min = max;
        if (isNaN(max)) max = min;
        if (max >= min && max > 0) tf = { min: min, max: max, kind: /deliver/i.test(d.kind) ? 'delivery' : /ship/i.test(d.kind) ? 'ship' : 'neutral' };
      } else {
        tf = utils.parseShipTimeframe(d.text);
        source = 'description';
      }
      var flag = this.parentElement && this.parentElement.querySelector('[data-ship-flag]');
      var range = function (t) { return t.min === t.max ? String(t.min) : t.min + '–' + t.max; };
      if (!tf || tf.ambiguous) {
        // never fall back to a default: the estimate stays hidden and the editor shows why
        if (flag && d.editor !== undefined) { flag.textContent = tf ? d.flagAmbiguous : d.flagNone; flag.hidden = false; }
        return;
      }
      var isHoliday = utils.holidayChecker(d.usHolidays === 'true', d.holidays);
      var cutoff = d.cutoff === '' || d.cutoff == null ? 14 : Number(d.cutoff);
      var now = Date.now(), offset = utils.parseOffset(d.offset);
      var w = utils.businessWindow(now, offset, tf.min, tf.max, cutoff, isHoliday);
      var thisYear = new Date(now + offset * 6e4).getUTCFullYear();
      var withYear = w.to.getUTCFullYear() !== thisYear || w.from.getUTCFullYear() !== thisYear;
      var opts = { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' };
      if (withYear) opts.year = 'numeric';
      var fmt;
      try { fmt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, opts); }
      catch (e) { fmt = new Intl.DateTimeFormat(undefined, opts); }
      this.querySelector('[data-ship-label]').textContent = d['label' + tf.kind.charAt(0).toUpperCase() + tf.kind.slice(1)] || '';
      this.querySelector('[data-ship-dates]').textContent = tf.min === tf.max ? fmt.format(w.to) : fmt.format(w.from) + ' – ' + fmt.format(w.to);
      this.querySelector('[data-ship-range]').textContent = (d.range || '').replace('[range]', range(tf));
      this.hidden = false;
      if (flag && d.editor !== undefined) { flag.textContent = d.flagOk.replace('[range]', range(tf)).replace('[source]', source); flag.hidden = false; }
    }
  }
  customElements.define('ship-estimate', ShipEstimate);

  /* Size & fit ----------------------------------------------------------
   * Recommends a size per size option from height & weight or the customer's
   * usual size (sets: top and bottom separately). Guide per size option: a linked
   * Size profile, else the category profile for the product type (sets: the option
   * name), else the estimated default guides. Unverified guides are labelled.
   * "Select" only checks the size in the product form; nothing is added to cart. */
  var SF_STORE = 'ozara:sizefit';
  var SF_BOTTOM = /pant|short|bottom|jogger|jean|trouser|legging|skirt/i;
  var SF_TOP = /hoodie|top|jacket|shirt|tee|crew|sweatshirt|zip|polo|tank/i;
  // Category profile from a product type or a size option name (Hoodie Size → hoodies)
  function sfCategoryByWords(text) {
    var t = String(text || '');
    if (/short/i.test(t)) return 'shorts';
    if (/pant|jogger|jean|trouser|bottom|denim/i.test(t)) return 'pants';
    if (/jacket|coat|outerwear/i.test(t)) return 'jackets';
    if (/hoodie|zip|sweatshirt|crew/i.test(t)) return 'hoodies';
    if (/tee|shirt|polo|tank|sleeve/i.test(t)) return 'tees';
    return null;
  }
  function sfEsc(t) { var d = document.createElement('div'); d.textContent = t == null ? '' : String(t); return d.innerHTML; }
  class SizeFit extends HTMLElement {
    connectedCallback() {
      try { this.cfg = JSON.parse(this.querySelector('[data-sf-config]').textContent); } catch (e) { return; }
      this.section = document.getElementById(this.dataset.section);
      this.picker = this.section && this.section.querySelector('variant-picker');
      if (!this.picker) return;
      this.parts = this.readParts();
      if (!this.parts.length) { this.closest('details').hidden = true; return; }
      this.unit = 'imperial';
      this.tab = 'body';
      this.buildUsual();
      this.renderAvailable();
      this.restore();
      var self = this;
      this.addEventListener('input', function (e) { if (e.target.matches('input, select')) { self.save(); self.update(); } });
      this.addEventListener('change', function (e) { if (e.target.matches('input, select')) { self.save(); self.update(); } });
      this.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-sf-tab]'), unit = e.target.closest('[data-sf-unit]'), act = e.target.closest('[data-sf-apply]');
        if (tab) self.setTab(tab.getAttribute('data-sf-tab'));
        if (unit) self.setUnit(unit.getAttribute('data-sf-unit'), true);
        if (act) self.apply(act.getAttribute('data-sf-apply') === 'notify');
      });
      // keep sold-out messages current when the shopper changes options themselves
      this.section.addEventListener('change', function (e) { if (!self.contains(e.target)) self.update(); });
      this.update();
    }
    readParts() {
      var cfg = this.cfg, fieldsets = $$('fieldset', this.picker), sizeSets = fieldsets.filter(function (f) { return f.hasAttribute('data-size-option'); });
      return sizeSets.map(function (fs, i) {
        var name = ((fs.querySelector('legend span') || {}).textContent || '').trim();
        var role = 'top';
        if (sizeSets.length > 1) role = SF_BOTTOM.test(name) ? 'bottom' : SF_TOP.test(name) ? 'top' : (i === 0 ? 'top' : 'bottom');
        var values = $$('input[type="radio"]', fs).map(function (inp) { return inp.value; });
        var waist = values.length > 0 && values.every(function (v) { return /^\d+(\.\d+)?$/.test(v.trim()); });
        var profile = sizeSets.length > 1 ? cfg.profiles[role] : (cfg.profiles.top || cfg.profiles.bottom);
        var guide = profile && profile.guide ? utils.parseFitGuide(profile.guide) : null;
        var estimate = !guide;
        // category profile: product type for single-size products, option name for sets
        var catId = null, type = String(cfg.type || '').trim().toLowerCase();
        if (sizeSets.length > 1) catId = sfCategoryByWords(name) || (role === 'bottom' ? 'pants' : 'hoodies');
        else {
          (cfg.categories || []).forEach(function (c) {
            if (!catId && String(c.types || '').toLowerCase().split(',').map(function (x) { return x.trim(); }).indexOf(type) !== -1) catId = c.id;
          });
          if (!catId) catId = sfCategoryByWords(type);
        }
        var cat = (cfg.categories || []).filter(function (c) { return c.id === catId; })[0] || null;
        if (!guide && cat && cat.guide) {
          var cg = utils.parseFitGuide(cat.guide);
          // only use it if its sizes are the same kind (letters vs waist numbers) as this option
          var sample = cg && (cg.type === 'grid' ? cg.rows[0].sizes[0] : cg.rows[0].size);
          if (cg && (/^\d+$/.test(String(sample).trim()) === waist)) { guide = cg; estimate = !cat.verified; }
        }
        if (!guide) guide = utils.parseFitGuide(waist ? cfg.grids.waist : cfg.grids.letter);
        if (!profile && cat) profile = { runs: cat.runs };
        var label = sizeSets.length > 1 ? (name.replace(/\s*size\s*/i, '').trim() || cfg.t[role]) : '';
        return { fs: fs, index: fieldsets.indexOf(fs), name: name, label: label, role: role, category: catId, values: values, waist: waist, profile: profile, guide: guide, estimate: estimate };
      });
    }
    buildUsual() {
      var cfg = this.cfg, box = this.querySelector('[data-sf-usual]'), html = '';
      var allWaist = this.parts.every(function (p) { return p.waist; });
      var tab = this.querySelector('[data-sf-tab="usual"]');
      tab.textContent = allWaist ? tab.getAttribute('data-label-waist') : tab.getAttribute('data-label-size');
      this.parts.forEach(function (p, i) {
        var lbl = (p.label ? p.label + ' · ' : '') + (p.waist ? cfg.t.usual_waist : cfg.t.usual_size);
        html += '<div class="sf__field"><label class="sf__label" for="SfU-' + i + '-' + p.fs.id + '">' + sfEsc(lbl) + '</label>';
        if (p.waist) {
          html += '<label class="sf__input"><input type="text" inputmode="numeric" maxlength="2" placeholder="32" data-sf-usual-part="' + i + '" id="SfU-' + i + '-' + p.fs.id + '"><span>in</span></label>';
        } else {
          html += '<select class="field__input sf__select" data-sf-usual-part="' + i + '" id="SfU-' + i + '-' + p.fs.id + '"><option value="">' + sfEsc(cfg.t.choose) + '</option>' +
            ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'].map(function (s) { return '<option>' + s + '</option>'; }).join('') + '</select>';
        }
        html += '</div>';
      });
      box.innerHTML = html;
    }
    setTab(tab) {
      this.tab = tab;
      $$('[data-sf-tab]', this).forEach(function (b) { var on = b.getAttribute('data-sf-tab') === tab; b.classList.toggle('is-active', on); b.setAttribute('aria-selected', String(on)); });
      $$('[data-sf-panel]', this).forEach(function (p) { p.hidden = p.getAttribute('data-sf-panel') !== tab; });
      this.save(); this.update();
    }
    setUnit(unit, convert) {
      if (unit === this.unit) return;
      var f = this.fields();
      if (convert) {
        if (unit === 'metric') {
          var hin = Number(f.ft.value) * 12 + Number(f.inch.value || 0);
          if (Number(f.ft.value)) f.cm.value = Math.round(hin * 2.54);
          if (Number(f.lb.value)) f.kg.value = Math.round(Number(f.lb.value) / 2.20462);
        } else {
          var tin = Math.round(Number(f.cm.value) / 2.54);
          if (Number(f.cm.value)) { f.ft.value = Math.floor(tin / 12); f.inch.value = tin % 12; }
          if (Number(f.kg.value)) f.lb.value = Math.round(Number(f.kg.value) * 2.20462);
        }
      }
      this.unit = unit;
      $$('[data-sf-unit]', this).forEach(function (b) { var on = b.getAttribute('data-sf-unit') === unit; b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', String(on)); });
      $$('[data-sf-units]', this).forEach(function (g) { g.hidden = g.getAttribute('data-sf-units') !== unit; });
      this.save(); this.update();
    }
    fields() {
      var q = function (n) { return this.querySelector('[name="' + n + '"]'); }.bind(this);
      return { ft: q('ft'), inch: q('inch'), lb: q('lb'), cm: q('cm'), kg: q('kg') };
    }
    pref() { var c = this.querySelector('.sf__prefs input:checked'); return c ? Number(c.value) : 0; }
    body() {
      var f = this.fields(), h, w;
      if (this.unit === 'metric') { h = Number(f.cm.value) / 2.54; w = Number(f.kg.value) * 2.20462; if (!(Number(f.cm.value) >= 120 && Number(f.cm.value) <= 230 && Number(f.kg.value) >= 30 && Number(f.kg.value) <= 230)) return null; }
      else {
        var ft = Number(f.ft.value), inch = f.inch.value === '' ? 0 : Number(f.inch.value);
        if (!(ft >= 4 && ft <= 7 && inch >= 0 && inch <= 11 && Number(f.lb.value) >= 70 && Number(f.lb.value) <= 500)) return null;
        h = ft * 12 + inch; w = Number(f.lb.value);
      }
      return { h: h, w: w };
    }
    recommend() {
      var self = this, pref = this.pref(), body = this.tab === 'body' ? this.body() : null, out = [];
      for (var i = 0; i < this.parts.length; i++) {
        var p = this.parts[i], base = null, outside = false;
        if (this.tab === 'body') {
          if (!body || !p.guide) return null;
          var fit = utils.lookupFit(p.guide, body.h, body.w);
          if (!fit) return null;
          base = fit.size; outside = fit.outside;
        } else {
          var el = this.querySelector('[data-sf-usual-part="' + i + '"]'), v = el ? String(el.value).trim() : '';
          if (!v || (p.waist && !(Number(v) >= 24 && Number(v) <= 50))) return null;
          base = v;
          var runs = String((p.profile && p.profile.runs) || 'true').toLowerCase();
          var m0 = utils.matchSize(p.values, base, runs.indexOf('small') === 0 ? 1 : runs.indexOf('large') === 0 ? -1 : 0);
          if (m0) base = m0.value;
        }
        var m = utils.matchSize(p.values, base, pref);
        if (!m) return null;
        out.push({ part: p, value: m.value, edge: m.edge, outside: outside });
      }
      return out;
    }
    variantFor(rec) {
      var opts = $$('fieldset', this.picker).map(function (fs) { var c = fs.querySelector('input:checked'); return c ? c.value : null; });
      rec.forEach(function (r) { opts[r.part.index] = r.value; });
      var matches = (this.picker.variants || []).filter(function (v) {
        return v.options.every(function (o, i) { return opts[i] == null || o === opts[i]; });
      });
      return { available: matches.some(function (v) { return v.available; }), any: matches.length > 0 };
    }
    update() {
      var cfg = this.cfg, t = cfg.t, res = this.querySelector('[data-sf-result]'), rec = this.recommend(), self = this;
      this.rec = rec;
      var est = this.querySelector('[data-sf-estimate]');
      var usesEstimate = this.tab === 'body' && this.parts.some(function (p) { return p.estimate; });
      est.textContent = cfg.estimate || ''; est.hidden = !(usesEstimate && cfg.estimate);
      if (!rec) {
        res.classList.remove('has-result');
        this.paint(res, '<p class="sf__empty">' + sfEsc(this.tab === 'body' ? t.empty_body : t.empty_usual) + '</p>');
        return;
      }
      var pref = this.pref(), why = this.tab === 'body' ? (pref < 0 ? t.why_closer : pref > 0 ? t.why_oversized : t.why_designed)
        : t.why_usual + (pref < 0 ? ' ' + t.why_closer : pref > 0 ? ' ' + t.why_oversized : '');
      var status = this.variantFor(rec), multi = rec.length > 1;
      var anyOutside = rec.some(function (r) { return r.outside || r.edge; });
      var html = '<div class="sf__sizes' + (multi ? ' sf__sizes--multi' : '') + '">';
      rec.forEach(function (r) {
        var note = r.outside ? t.why_outside : r.edge ? t.why_edge.replace('[edge]', r.edge === 'small' ? t.edge_small : t.edge_large) : '';
        html += '<div class="sf__size-box"><span class="sf__label">' + sfEsc(multi ? r.part.label || t[r.part.role] : (anyOutside ? t.closest : t.your_size)) + '</span>' +
          '<strong class="sf__size">' + sfEsc(r.value) + '</strong>' +
          (multi && note ? '<span class="sf__part-note">' + sfEsc(note) + '</span>' : '') + '</div>';
      });
      html += '</div>';
      var notes = [why];
      if (!multi) { var r0 = rec[0]; if (r0.outside) notes.push(t.why_outside); else if (r0.edge) notes.push(t.why_edge.replace('[edge]', r0.edge === 'small' ? t.edge_small : t.edge_large)); }
      rec.forEach(function (r) { if (r.part.profile && r.part.profile.note) notes.push(r.part.profile.note); });
      html += '<p class="sf__why">' + sfEsc(notes.join(' ')) + '</p>';
      var sizeText = rec.map(function (r) { return r.value; }).join(' / ');
      if (status.any && !status.available) {
        html += '<p class="sf__soldout">' + sfEsc(t.sold_out.replace('[size]', sizeText)) + '</p>' +
          '<button type="button" class="btn btn--block btn--secondary sf__btn" data-sf-apply="notify">' + sfEsc(t.notify) + '</button>';
      } else {
        html += '<button type="button" class="btn btn--block sf__btn" data-sf-apply="select">' + sfEsc(multi ? t.select_both : t.select.replace('[size]', sizeText)) + '</button>';
      }
      html += '<p class="sf__done" data-sf-done hidden>' + sfEsc(t.selected) + '</p>';
      this.paint(res, html);
      res.classList.add('has-result');
    }
    // Only replace the result when it changed, so a tap that blurs an input still lands on the button
    paint(el, html) {
      if (this.painted === html) return;
      this.painted = html;
      el.innerHTML = html;
    }
    apply(notify) {
      var rec = this.rec, self = this;
      if (!rec) return;
      rec.forEach(function (r) {
        var input = $$('input[type="radio"]', r.part.fs).filter(function (i) { return i.value === r.value; })[0];
        if (input && !input.checked) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
      });
      var done = this.querySelector('[data-sf-done]');
      if (notify) {
        var bis = this.section.querySelector('[data-bis-container]') || this.section.querySelector('product-form');
        if (bis) bis.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        if (done) done.hidden = false;
        var first = rec[0] && rec[0].part.fs;
        if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    renderAvailable() {
      var t = this.cfg.t, multi = this.parts.length > 1;
      this.querySelector('[data-sf-available]').textContent = t.available.replace('[sizes]', this.parts.map(function (p) {
        return (multi ? p.label + ': ' : '') + p.values.join(', ');
      }).join(' · '));
    }
    save() {
      var f = this.fields(), usual = {};
      $$('[data-sf-usual-part]', this).forEach(function (el) { usual[el.tagName === 'SELECT' ? 'letter' : 'waist'] = el.value; });
      var data = { unit: this.unit, tab: this.tab, pref: this.pref(), ft: f.ft.value, inch: f.inch.value, lb: f.lb.value, cm: f.cm.value, kg: f.kg.value, usual: usual };
      try { localStorage.setItem(SF_STORE, JSON.stringify(data)); } catch (e) { /* private mode */ }
    }
    restore() {
      var d = null;
      try { d = JSON.parse(localStorage.getItem(SF_STORE) || 'null'); } catch (e) { d = null; }
      if (!d) return;
      var f = this.fields();
      ['ft', 'inch', 'lb', 'cm', 'kg'].forEach(function (k) { if (d[k] != null) f[k].value = d[k]; });
      var pr = this.querySelector('.sf__prefs input[value="' + Number(d.pref || 0) + '"]');
      if (pr) pr.checked = true;
      $$('[data-sf-usual-part]', this).forEach(function (el) { var v = d.usual && d.usual[el.tagName === 'SELECT' ? 'letter' : 'waist']; if (v) el.value = v; });
      if (d.unit === 'metric') this.setUnit('metric', false);
      if (d.tab === 'usual') this.setTab('usual');
    }
  }
  customElements.define('size-fit', SizeFit);
  // "What's my size?" opens the Size & fit accordion and moves focus into it
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-sf-open]');
    if (!link) return;
    var d = document.getElementById(link.getAttribute('data-sf-open'));
    if (!d) return;
    e.preventDefault();
    d.open = true;
    d.scrollIntoView({ behavior: 'smooth', block: 'start' });
    var first = d.querySelector('.sf__panel:not([hidden]) input, .sf__panel:not([hidden]) select');
    if (first) setTimeout(function () { first.focus({ preventScroll: true }); }, 350);
  });

  /* Fit finder --------------------------------------------------------- */
  class FitFinder extends HTMLElement {
    connectedCallback() {
      this.rows = utils.parseFitTable(this.querySelector('[data-fit-table]').textContent);
      this.form = this.querySelector('form');
      this.result = this.querySelector('[data-fit-result]');
      this.unit = 'imperial';
      var self = this;
      this.form.addEventListener('input', function () { self.calculate(); });
      this.form.addEventListener('change', function () { self.calculate(); });
      $$('[data-unit]', this).forEach(function (b) {
        b.addEventListener('click', function () {
          self.unit = b.getAttribute('data-unit');
          $$('[data-unit]', self).forEach(function (o) { o.classList.toggle('is-active', o === b); o.setAttribute('aria-pressed', String(o === b)); });
          $$('[data-units]', self).forEach(function (g) { g.hidden = g.getAttribute('data-units') !== self.unit; });
          self.calculate();
        });
      });
      this.form.addEventListener('submit', function (e) { e.preventDefault(); self.selectRecommended(); });
    }
    calculate() {
      var f = this.form.elements, h, w;
      if (this.unit === 'metric') {
        h = Number(f.cm.value) / 2.54; w = Number(f.kg.value) * 2.20462;
      } else {
        h = Number(f.ft.value) * 12 + Number(f.inch.value || 0); w = Number(f.lb.value);
      }
      var pref = Number((this.form.querySelector('[name="pref"]:checked') || {}).value || 0);
      var rec = utils.recommendSize(this.rows, h, w, pref);
      var sizeEl = this.result.querySelector('[data-fit-size]');
      var noteEl = this.result.querySelector('[data-fit-note]');
      var btn = this.result.querySelector('button');
      if (!rec) { sizeEl.textContent = '—'; noteEl.textContent = noteEl.dataset.empty; btn.hidden = true; this.rec = null; return; }
      this.rec = rec;
      sizeEl.textContent = rec.size;
      noteEl.textContent = rec.exact ? noteEl.dataset.exact : noteEl.dataset.approx;
      btn.hidden = !this.findInput(rec.size);
    }
    findInput(size) {
      var section = document.getElementById(this.dataset.section);
      if (!section) return null;
      return $$('variant-picker fieldset[data-size-option] input', section).filter(function (i) {
        return i.value.trim().toLowerCase() === size.trim().toLowerCase();
      })[0] || null;
    }
    selectRecommended() {
      if (!this.rec) return;
      var input = this.findInput(this.rec.size);
      if (!input) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      closeDialog(this.closest('dialog'));
    }
  }
  customElements.define('fit-finder', FitFinder);

  /* Complete the fit bundle ------------------------------------------- */
  class FitBundle extends HTMLElement {
    connectedCallback() {
      this.selects = $$('select[data-bundle-variant]', this);
      this.total = this.querySelector('[data-bundle-total]');
      this.button = this.querySelector('[data-bundle-add]');
      var self = this;
      this.addEventListener('change', function () { self.refresh(); });
      if (this.button) this.button.addEventListener('click', function (e) { e.preventDefault(); self.addAll(); });
      this.refresh();
    }
    chosen() {
      return this.selects.map(function (s) {
        var o = s.options[s.selectedIndex];
        return o && o.value ? { id: Number(o.value), price: Number(o.getAttribute('data-price') || 0) } : null;
      });
    }
    refresh() {
      var chosen = this.chosen();
      var picked = chosen.filter(Boolean);
      var sum = picked.reduce(function (a, c) { return a + c.price; }, 0);
      if (this.total) this.total.textContent = theme.formatMoney(sum);
      if (this.button) {
        var label = this.button.querySelector('[data-bundle-label]');
        var missing = chosen.length - picked.length;
        label.textContent = missing ? this.button.dataset.textSelect.replace('[count]', missing) : this.button.dataset.textAdd.replace('[count]', picked.length);
        this.button.setAttribute('aria-disabled', picked.length ? 'false' : 'true');
      }
    }
    addAll() {
      var items = this.chosen().filter(Boolean).map(function (c) { return { id: c.id, quantity: 1 }; });
      if (!items.length) {
        var first = this.selects.filter(function (s) { return !s.value; })[0];
        if (first) first.focus();
        return;
      }
      var btn = this.button;
      btn.classList.add('is-loading');
      Cart.add(items).then(Cart.afterAdd).catch(function (err) { toast(err.message); })
        .finally(function () { btn.classList.remove('is-loading'); });
    }
  }
  customElements.define('fit-bundle', FitBundle);

  /* Build Your Fit: one top + one bottom, added together as a bundle ----- */
  utils.bundleSavings = function (prices, percent) {
    // Shopify allocates a percentage discount per line, rounded to the cent
    return prices.reduce(function (sum, cents) { return sum + Math.round(cents * percent / 100); }, 0);
  };

  class BuildYourFit extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.percent = Number(this.dataset.percent) || 0;
      this.panels = {};
      $$('[data-byf-panel]', this).forEach(function (panel) {
        var role = panel.getAttribute('data-byf-panel');
        var products = {};
        $$('script[data-byf-product]', panel).forEach(function (s) {
          try { var p = JSON.parse(s.textContent); products[p.id] = p; } catch (err) { /* skip malformed */ }
        });
        self.panels[role] = { el: panel, products: products, product: null, selected: [], variant: null };
        var first = $('[data-byf-pick]:not([data-sold-out])', panel) || $('[data-byf-pick]', panel);
        if (first) self.pick(role, first.getAttribute('data-byf-pick'));
      });
      this.addEventListener('click', function (e) {
        var pick = e.target.closest('[data-byf-pick]');
        if (pick) { self.pick(pick.closest('[data-byf-panel]').getAttribute('data-byf-panel'), pick.getAttribute('data-byf-pick')); return; }
        var opt = e.target.closest('[data-byf-option]');
        if (opt && !opt.disabled) {
          var role = opt.closest('[data-byf-panel]').getAttribute('data-byf-panel');
          self.panels[role].selected[Number(opt.getAttribute('data-byf-option'))] = opt.getAttribute('data-value');
          self.resolve(role);
          return;
        }
        if (e.target.closest('[data-byf-add]')) { e.preventDefault(); self.add(); }
      });
      this.update();
    }

    pick(role, id) {
      var state = this.panels[role];
      var product = state.products[id];
      if (!product) return;
      state.product = product;
      state.selected = [];
      // Preselect every option except size, so shoppers only choose a size
      var sizeIndex = this.sizeIndex(product);
      var firstAvailable = product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];
      product.options.forEach(function (name, i) { if (i !== sizeIndex) state.selected[i] = firstAvailable.options[i]; });
      if (product.variants.length === 1) state.selected = product.variants[0].options.slice();
      $$('[data-byf-pick]', state.el).forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-byf-pick') === String(id))); });
      this.resolve(role);
    }

    sizeIndex(product) {
      var idx = -1;
      product.options.forEach(function (name, i) { if (idx < 0 && /size|waist|length|fit/i.test(name)) idx = i; });
      return idx < 0 ? product.options.length - 1 : idx;
    }

    resolve(role) {
      var state = this.panels[role];
      var sel = state.selected;
      state.variant = state.product.variants.filter(function (v) {
        return v.options.every(function (val, i) { return sel[i] === val; });
      })[0] || null;
      this.renderPanel(role);
      this.update();
    }

    renderPanel(role) {
      var state = this.panels[role], p = state.product, v = state.variant, el = state.el;
      var img = (v && v.image) || p.image;
      var media = $('[data-byf-media]', el);
      if (img) {
        var current = $('img', media);
        if (!current || current.getAttribute('src') !== img.src) {
          var tag = document.createElement('img');
          tag.src = img.src; tag.srcset = img.srcset || ''; tag.sizes = '(min-width: 990px) 40vw, 90vw';
          tag.alt = p.image ? p.image.alt : p.title; tag.className = 'is-entering';
          media.innerHTML = ''; media.appendChild(tag);
          requestAnimationFrame(function () { tag.classList.remove('is-entering'); });
        }
      }
      var title = $('[data-byf-title]', el);
      title.textContent = p.title; title.href = p.url;
      var price = v ? v.price : Math.min.apply(null, p.variants.map(function (x) { return x.price; }));
      $('[data-byf-price]', el).textContent = theme.formatMoney(price);

      var sel = state.selected, self = this;
      var html = p.variants.length === 1 && p.options.length === 1 && p.variants[0].title === 'Default Title' ? '' :
        p.options.map(function (name, i) {
          var values = [];
          p.variants.forEach(function (x) { if (values.indexOf(x.options[i]) < 0) values.push(x.options[i]); });
          var buttons = values.map(function (val) {
            var possible = p.variants.some(function (x) {
              return x.available && x.options[i] === val && x.options.every(function (o, j) { return j === i || sel[j] === undefined || sel[j] === o; });
            });
            var on = sel[i] === val;
            return '<button type="button" class="byf-option' + (on ? ' is-selected' : '') + '" data-byf-option="' + i + '" data-value="' + self.esc(val) + '"' +
              (possible ? '' : ' disabled') + ' aria-pressed="' + on + '">' + self.esc(val) + '</button>';
          }).join('');
          return '<div class="byf-option-group" role="group" aria-label="' + self.esc(name) + '"><span class="byf-option-group__name">' + self.esc(name) + '</span><div class="byf-option-group__values">' + buttons + '</div></div>';
        }).join('');
      $('[data-byf-options]', el).innerHTML = html;

      var stock = $('[data-byf-stock]', el), d = this.dataset;
      stock.className = 'byf-panel__stock';
      if (!v) stock.textContent = p.available ? '' : d.textSoldOut;
      else if (!v.available) { stock.textContent = d.textSoldOut; stock.classList.add('is-out'); }
      else if (v.low > 0) { stock.textContent = d.textOnlyLeft.replace('[count]', v.low); stock.classList.add('is-low'); }
      else { stock.textContent = d.textInStock; stock.classList.add('is-in'); }
    }

    chosen() {
      var top = this.panels.top && this.panels.top.variant, bottom = this.panels.bottom && this.panels.bottom.variant;
      return { top: top && top.available ? top : null, bottom: bottom && bottom.available ? bottom : null };
    }

    update() {
      var c = this.chosen(), d = this.dataset, q = function (s, ctx) { return ctx.querySelector(s); };
      var prices = [c.top, c.bottom].filter(Boolean).map(function (v) { return v.price; });
      var original = prices.reduce(function (a, b) { return a + b; }, 0);
      var both = c.top && c.bottom;
      var savings = both ? utils.bundleSavings(prices, this.percent) : 0;
      var set = function (sel, text) { var el = q(sel, this); if (el) el.textContent = text; }.bind(this);
      set('[data-byf-original]', prices.length ? theme.formatMoney(original) : '—');
      set('[data-byf-savings]', both ? '−' + theme.formatMoney(savings) : '—');
      set('[data-byf-total]', prices.length ? theme.formatMoney(original - savings) : '—');
      var youSave = q('[data-byf-you-save]', this);
      if (youSave) { youSave.hidden = !(both && savings > 0); youSave.textContent = d.textSave.replace('[amount]', theme.formatMoney(savings)); }

      var btn = q('[data-byf-add]', this), label = q('[data-byf-label]', this);
      var missing = !c.top ? d.textSelectTop : !c.bottom ? d.textSelectBottom : '';
      label.textContent = missing || d.textAdd;
      btn.setAttribute('aria-disabled', both ? 'false' : 'true');
    }

    add() {
      var c = this.chosen(), btn = $('[data-byf-add]', this), note = $('[data-byf-note]', this);
      if (!c.top || !c.bottom) {
        var role = !c.top ? 'top' : 'bottom';
        var target = $('.byf-option:not([disabled]):not(.is-selected)', this.panels[role].el) || this.panels[role].el;
        target.focus && target.focus();
        return;
      }
      var bundle = 'BYF-' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).slice(2, 6).toUpperCase();
      var items = [
        { id: c.top.id, quantity: 1, properties: { _ozara_bundle: bundle, _ozara_bundle_role: 'top' } },
        { id: c.bottom.id, quantity: 1, properties: { _ozara_bundle: bundle, _ozara_bundle_role: 'bottom' } }
      ];
      btn.classList.add('is-loading');
      if (note) note.hidden = true;
      var self = this;
      Cart.add(items).then(function (data) {
        Cart.afterAdd(data);
        return self.verify(bundle);
      }).catch(function (err) {
        toast(err.message);
      }).finally(function () { btn.classList.remove('is-loading'); });
    }

    // Read the real cart and report the discount Shopify actually applied to this bundle
    verify(bundle) {
      var note = $('[data-byf-note]', this), d = this.dataset, percent = this.percent;
      if (!note) return Promise.resolve();
      if (d.discountAt !== 'cart') {
        // Discount applied at checkout (e.g. Bundler): Shopify's cart can't show it yet
        note.classList.remove('is-ok');
        note.textContent = percent > 0 ? d.textAppliedCheckout : d.textAdded;
        note.hidden = false;
        return Promise.resolve();
      }
      return fetch(theme.routes.cart + '.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var lines = (cart.items || []).filter(function (l) { return l.properties && l.properties._ozara_bundle === bundle; });
          var saved = lines.reduce(function (sum, l) {
            return sum + (l.line_level_discount_allocations || []).reduce(function (a, x) { return a + Number(x.amount || 0); }, 0);
          }, 0);
          note.classList.toggle('is-ok', saved > 0);
          if (saved > 0) note.textContent = d.textApplied.replace('[amount]', theme.formatMoney(saved));
          else note.textContent = percent > 0 ? d.textNotApplied : d.textAdded;
          note.hidden = false;
        })
        .catch(function () { /* the cart drawer still shows the real totals */ });
    }

    esc(s) { return String(s).replace(/[&<>"']/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]; }); }
  }
  customElements.define('build-your-fit', BuildYourFit);

  /* Build Your Tee Bundle ------------------------------------------------
   * Three slots filled from one shared tee picker; each slot picks its own size.
   * On load (and with Shuffle) the slots get 3 different random in-stock tees in
   * their first available size; nothing is added until the shopper clicks Add.
   * The active slot is the first empty one, or the slot the shopper taps to swap.
   * Adds all tees together with _ozara_bundle / _ozara_bundle_role: tee. The
   * bundle price shown is display only; Bundler applies it at checkout. */
  class TeeBundle extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.size = Number(this.dataset.size) || 3;
      this.bundlePrice = Number(this.dataset.bundlePrice) || 0;
      this.showPrice = this.dataset.showPrice === 'true';
      this.products = {};
      $$('script[data-byf-product]', this).forEach(function (s) {
        try { var p = JSON.parse(s.textContent); self.products[p.id] = p; } catch (err) { /* skip malformed */ }
      });
      this.slots = $$('[data-tb-slot]', this).map(function (el) { return { el: el, product: null, selected: [], variant: null }; });
      this.active = 0;
      this.addEventListener('click', function (e) {
        var pick = e.target.closest('[data-byf-pick]');
        if (pick) { self.fill(self.active, pick.getAttribute('data-byf-pick')); return; }
        var opt = e.target.closest('[data-byf-option]');
        if (opt && !opt.disabled) {
          var i = Number(opt.closest('[data-tb-slot]').getAttribute('data-tb-slot'));
          self.slots[i].selected[Number(opt.getAttribute('data-byf-option'))] = opt.getAttribute('data-value');
          self.resolve(i);
          return;
        }
        var focus = e.target.closest('[data-tb-focus]');
        if (focus) { self.setActive(Number(focus.closest('[data-tb-slot]').getAttribute('data-tb-slot')), true); return; }
        var rm = e.target.closest('[data-tb-remove]');
        if (rm) { self.clear(Number(rm.closest('[data-tb-slot]').getAttribute('data-tb-slot'))); return; }
        if (e.target.closest('[data-tb-shuffle]')) { self.shuffle(); return; }
        if (e.target.closest('[data-tb-add]')) { e.preventDefault(); self.add(); }
      });
      this.shuffle();
    }
    eligible() {
      var self = this;
      return Object.keys(this.products).map(function (id) { return self.products[id]; })
        .filter(function (p) { return p.available && p.variants.some(function (v) { return v.available; }); });
    }
    // Fill every slot with different random in-stock tees (repeats only if fewer tees
    // than slots are available). Avoids handing back the same set on Shuffle.
    shuffle() {
      var pool = this.eligible(), size = this.slots.length, self = this, picks = [];
      var current = this.slots.map(function (s) { return s.product ? String(s.product.id) : ''; }).sort().join();
      for (var attempt = 0; attempt < 6; attempt++) {
        var bag = pool.slice();
        for (var i = bag.length - 1; i > 0; i--) { var j = randomInt(i + 1), t = bag[i]; bag[i] = bag[j]; bag[j] = t; }
        picks = [];
        for (var k = 0; k < size; k++) picks.push(bag.length ? bag[k % bag.length] : null);
        if (pool.length <= size || picks.map(function (p) { return String(p.id); }).sort().join() !== current) break;
      }
      picks.forEach(function (p, i) { if (p) self.fill(i, p.id); else self.clear(i); });
      this.setActive(0, false);
    }
    firstEmpty() {
      for (var i = 0; i < this.slots.length; i++) if (!this.slots[i].product) return i;
      return -1;
    }
    setActive(i, scroll) {
      this.active = i;
      var d = this.dataset, filled = !!this.slots[i].product;
      this.slots.forEach(function (s, j) { s.el.classList.toggle('is-active', j === i); });
      var label = $('[data-tb-picker-label]', this);
      if (label) label.textContent = (filled ? d.textSwap : d.textChoose).replace('[n]', i + 1);
      var current = this.slots[i].product ? String(this.slots[i].product.id) : '';
      $$('[data-byf-pick]', this).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-byf-pick') === current ? 'true' : 'false'); });
      var picker = $('.tb-picker', this);
      if (scroll && picker && picker.getBoundingClientRect().top > window.innerHeight * .8) picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    sizeIndex(product) {
      var idx = -1;
      product.options.forEach(function (name, i) { if (idx < 0 && /size|waist|length|fit/i.test(name)) idx = i; });
      return idx < 0 ? product.options.length - 1 : idx;
    }
    fill(i, id) {
      var product = this.products[id], slot = this.slots[i];
      if (!product || !slot) return;
      // keep the slot's current size when the new tee has it in stock, else its first available size
      var prefer = slot.variant ? slot.variant.options[this.sizeIndex(slot.product)] : null;
      slot.product = product;
      slot.selected = [];
      var sizeIndex = this.sizeIndex(product);
      var inStock = product.variants.filter(function (v) { return v.available; });
      var chosen = inStock.filter(function (v) { return prefer != null && v.options[sizeIndex] === prefer; })[0] || inStock[0];
      if (chosen) slot.selected = chosen.options.slice();
      else {
        product.options.forEach(function (name, k) { if (k !== sizeIndex) slot.selected[k] = product.variants[0].options[k]; });
        if (product.variants.length === 1) slot.selected = product.variants[0].options.slice();
      }
      this.resolve(i);
      var next = this.firstEmpty();
      this.setActive(next < 0 ? i : next, false);
    }
    clear(i) {
      var slot = this.slots[i];
      slot.product = null; slot.selected = []; slot.variant = null;
      this.renderSlot(i);
      this.setActive(i, false);
      this.update();
    }
    resolve(i) {
      var slot = this.slots[i], sel = slot.selected;
      slot.variant = slot.product.variants.filter(function (v) {
        return v.options.every(function (val, k) { return sel[k] === val; });
      })[0] || null;
      this.renderSlot(i);
      this.update();
    }
    renderSlot(i) {
      var slot = this.slots[i], p = slot.product, v = slot.variant, el = slot.el, d = this.dataset, self = this;
      el.toggleAttribute('data-empty', !p);
      var media = $('[data-byf-media]', el), title = $('[data-byf-title]', el), stock = $('[data-byf-stock]', el);
      $('[data-tb-remove]', el).hidden = !p;
      $('[data-tb-empty]', el).hidden = !!p;
      title.hidden = !p;
      if (!p) {
        media.innerHTML = '<span class="tb-slot__plus" aria-hidden="true">+</span>';
        $('[data-byf-price]', el).textContent = '';
        $('[data-byf-options]', el).innerHTML = '';
        stock.textContent = ''; stock.className = 'byf-panel__stock';
        return;
      }
      var img = (v && v.image) || p.image;
      var current = $('img', media);
      if (img && (!current || current.getAttribute('src') !== img.src)) {
        var tag = document.createElement('img');
        tag.src = img.src; tag.srcset = img.srcset || ''; tag.sizes = '(min-width: 990px) 25vw, 30vw';
        tag.alt = ''; tag.className = 'is-entering';
        media.innerHTML = ''; media.appendChild(tag);
        requestAnimationFrame(function () { tag.classList.remove('is-entering'); });
      }
      title.textContent = p.title; title.href = p.url;
      var price = v ? v.price : Math.min.apply(null, p.variants.map(function (x) { return x.price; }));
      $('[data-byf-price]', el).textContent = theme.formatMoney(price);
      var sel = slot.selected;
      var html = p.variants.length === 1 && p.options.length === 1 && p.variants[0].title === 'Default Title' ? '' :
        p.options.map(function (name, k) {
          var values = [];
          p.variants.forEach(function (x) { if (values.indexOf(x.options[k]) < 0) values.push(x.options[k]); });
          var buttons = values.map(function (val) {
            var possible = p.variants.some(function (x) {
              return x.available && x.options[k] === val && x.options.every(function (o, j) { return j === k || sel[j] === undefined || sel[j] === o; });
            });
            var on = sel[k] === val;
            return '<button type="button" class="byf-option' + (on ? ' is-selected' : '') + '" data-byf-option="' + k + '" data-value="' + self.esc(val) + '"' +
              (possible ? '' : ' disabled') + ' aria-pressed="' + on + '">' + self.esc(val) + '</button>';
          }).join('');
          return '<div class="byf-option-group" role="group" aria-label="' + self.esc(name) + '"><span class="byf-option-group__name">' + self.esc(name) + '</span><div class="byf-option-group__values">' + buttons + '</div></div>';
        }).join('');
      $('[data-byf-options]', el).innerHTML = html;
      stock.className = 'byf-panel__stock';
      if (!v) stock.textContent = p.available ? '' : d.textSoldOut;
      else if (!v.available) { stock.textContent = d.textSoldOut; stock.classList.add('is-out'); }
      else if (v.low > 0) { stock.textContent = d.textOnlyLeft.replace('[count]', v.low); stock.classList.add('is-low'); }
      else { stock.textContent = d.textInStock; stock.classList.add('is-in'); }
    }
    ready() { return this.slots.filter(function (s) { return s.variant && s.variant.available; }); }
    update() {
      var d = this.dataset, ready = this.ready(), n = ready.length, all = n === this.size;
      var count = $('[data-tb-count]', this);
      if (count) count.textContent = d.textCount.replace('[count]', n);
      $$('[data-tb-seg]', this).forEach(function (seg, i) { seg.classList.toggle('is-done', i < n); });
      var regular = ready.reduce(function (a, s) { return a + s.variant.price; }, 0);
      var saving = regular - this.bundlePrice, showSave = this.showPrice && all && saving > 0;
      var reg = $('[data-tb-regular]', this);
      // with the bundle price shown, only strike through a complete 3-tee total
      if (reg) reg.textContent = (this.showPrice ? all : n) ? money(regular) : '—';
      var row = $('[data-tb-save-row]', this);
      if (row) {
        row.hidden = !showSave;
        $('[data-tb-save-amount]', row).textContent = money(Math.max(0, saving));
        $('[data-tb-pct]', row).textContent = d.textPercent.replace('[percent]', regular ? Math.round(saving * 100 / regular) : 0);
      }
      var youSave = $('[data-tb-you-save]', this);
      if (youSave) {
        youSave.hidden = !showSave;
        youSave.textContent = d.textSave.replace('[amount]', money(Math.max(0, saving)));
      }
      var label = $('[data-tb-label]', this), btn = $('[data-tb-add]', this);
      var empty = this.slots.filter(function (s) { return !s.product; }).length;
      var needsSize = this.slots.map(function (s, i) { return s.product && !(s.variant && s.variant.available) ? i : -1; }).filter(function (i) { return i >= 0; });
      label.textContent = all ? d.textAdd : empty ? d.textChooseMore.replace('[count]', empty) : d.textSelectSize.replace('[n]', needsSize[0] + 1);
      btn.setAttribute('aria-disabled', all ? 'false' : 'true');
    }
    add() {
      var ready = this.ready(), btn = $('[data-tb-add]', this), note = $('[data-tb-note]', this), d = this.dataset;
      if (ready.length !== this.size) {
        var i = this.firstEmpty();
        if (i >= 0) { this.setActive(i, true); return; }
        var missing = this.slots.filter(function (s) { return !(s.variant && s.variant.available); })[0];
        var target = missing && ($('.byf-option:not([disabled]):not(.is-selected)', missing.el) || missing.el);
        if (target && target.focus) target.focus();
        return;
      }
      var bundle = 'TEE-' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).slice(2, 6).toUpperCase();
      var items = ready.map(function (s) { return { id: s.variant.id, quantity: 1, properties: { _ozara_bundle: bundle, _ozara_bundle_role: 'tee' } }; });
      btn.classList.add('is-loading');
      if (note) note.hidden = true;
      var self = this;
      Cart.add(items).then(function (data) {
        Cart.afterAdd(data);
        if (note) { note.textContent = self.showPrice ? d.textAddedVerified : d.textAdded; note.hidden = false; }
      }).catch(function (err) {
        toast(err.message);
      }).finally(function () { btn.classList.remove('is-loading'); });
    }
    esc(s) { return String(s).replace(/[&<>"']/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]; }); }
  }
  customElements.define('tee-bundle', TeeBundle);

  function randomInt(n) {
    try { var a = new Uint32Array(1); window.crypto.getRandomValues(a); return a[0] % n; } catch (err) { return Math.floor(Math.random() * n); }
  }
  // $99.00 → $99 for whole amounts; keeps cents otherwise
  function money(cents) { return theme.formatMoney(cents).replace(/([.,])00(?!\d)/, ''); }

  /* Product rail controls ---------------------------------------------- */
  class ProductRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.rail');
      this.bar = this.querySelector('.rail-progress span');
      if (!this.track) return;
      var self = this;
      var sync = function () {
        var max = self.track.scrollWidth - self.track.clientWidth;
        if (!self.bar) return;
        var ratio = self.track.clientWidth / Math.max(1, self.track.scrollWidth);
        self.bar.style.width = (ratio * 100) + '%';
        self.bar.style.transform = 'translateX(' + (max > 0 ? (self.track.scrollLeft / max) * ((1 / ratio) - 1) * 100 : 0) + '%)';
        if (self.bar.parentElement) self.bar.parentElement.hidden = max <= 4;
      };
      // Dim an arrow when there is nothing further to scroll that way
      var arrows = function () {
        var max = self.track.scrollWidth - self.track.clientWidth;
        $$('[data-rail-dir]', self).forEach(function (b) {
          var back = Number(b.getAttribute('data-rail-dir')) < 0;
          b.disabled = back ? self.track.scrollLeft <= 4 : self.track.scrollLeft >= max - 4;
        });
      };
      this.track.addEventListener('scroll', sync, { passive: true });
      this.track.addEventListener('scroll', utils.debounce(arrows, 60), { passive: true });
      window.addEventListener('resize', utils.debounce(function () { sync(); arrows(); }, 100));
      sync();
      arrows();
      $$('[data-rail-dir]', this).forEach(function (b) {
        b.addEventListener('click', function () {
          self.track.scrollBy({ left: Number(b.getAttribute('data-rail-dir')) * self.track.clientWidth * 0.8, behavior: 'smooth' });
        });
      });
    }
  }
  customElements.define('product-rail', ProductRail);

  /* Community: photo slider ---------------------------------------------
   * Native horizontal scroll with centre snapping (smooth touch swiping),
   * plus arrows, dots and tap-to-centre. The slide nearest the centre is active. */
  class CommunityFits extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.track = $('.fits__track', this);
      this.slides = $$('[data-fits-slide]', this);
      this.dots = $$('.fits__dot', this);
      this.prev = $('[data-fits-dir="-1"]', this);
      this.next = $('[data-fits-dir="1"]', this);
      if (!this.track || !this.slides.length) return;
      this.index = Math.min(Number(this.dataset.start) || 0, this.slides.length - 1);
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.goTo(this.index, false);
      var ticking = false;
      this.track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { ticking = false; self.sync(); });
      }, { passive: true });
      this.addEventListener('click', function (e) {
        var dir = e.target.closest('[data-fits-dir]');
        if (dir) { self.goTo(self.index + Number(dir.getAttribute('data-fits-dir')), true); return; }
        var go = e.target.closest('[data-fits-go]');
        if (go) self.goTo(Number(go.getAttribute('data-fits-go')), true);
      });
      window.addEventListener('resize', utils.debounce(function () { self.goTo(self.index, false); }, 150));
      // Theme editor: show the photo block being edited
      document.addEventListener('shopify:block:select', function (e) {
        var i = self.slides.indexOf(e.target);
        if (i > -1) self.goTo(i, true);
      });
    }
    goTo(i, smooth) {
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      var slide = this.slides[i];
      var left = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
      this.track.scrollTo({ left: left, behavior: smooth && !this.reduced ? 'smooth' : 'auto' });
      this.setActive(i);
    }
    sync() {
      var centre = this.track.scrollLeft + this.track.clientWidth / 2;
      var best = 0, bestDist = Infinity;
      this.slides.forEach(function (s, i) {
        var d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - centre);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      if (best !== this.index) this.setActive(best);
    }
    setActive(i) {
      this.index = i;
      this.slides.forEach(function (s, j) {
        var on = j === i;
        s.classList.toggle('is-active', on);
        var photo = $('.fits__photo', s);
        if (photo) photo.tabIndex = on ? -1 : 0;
      });
      this.dots.forEach(function (d, j) { d.setAttribute('aria-selected', String(j === i)); });
      if (this.prev) this.prev.disabled = i === 0;
      if (this.next) this.next.disabled = i === this.slides.length - 1;
    }
  }
  customElements.define('community-fits', CommunityFits);

  /* Product recommendations (lazy) ------------------------------------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      var self = this;
      var load = function () {
        fetch(self.dataset.url).then(function (r) { return r.text(); }).then(function (html) {
          var fresh = parseHTML(html).querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) self.innerHTML = fresh.innerHTML;
        }).catch(function () {});
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      var io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) { io.disconnect(); load(); }
      }, { rootMargin: '0px 0px 600px 0px' });
      io.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* Collection filters + sort (Section Rendering API) ------------------ */
  class CollectionFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = document.getElementById('FacetsForm-' + this.sectionId);
      var self = this;
      var onChange = utils.debounce(function () { self.submitForm(); }, 350);
      if (this.form) {
        this.form.addEventListener('change', onChange);
        this.form.addEventListener('submit', function (e) { e.preventDefault(); self.submitForm(); closeDialog(self.form.closest('dialog')); });
      }
      $$('[form="FacetsForm-' + this.sectionId + '"]').forEach(function (el) { el.addEventListener('change', onChange); });
      this.addEventListener('click', function (e) {
        var link = e.target.closest('[data-facet-link]');
        if (!link) return;
        e.preventDefault();
        self.render(link.getAttribute('href'));
      });
      window.addEventListener('popstate', function () { self.render(window.location.href, false); });
      // Toolbar sort dropdown: mirror the choice into the drawer's sort radios, then apply
      this.addEventListener('change', function (e) {
        var select = e.target.closest('[data-sort-by]');
        if (!select || !self.form) return;
        var radio = $$('input[name="sort_by"]', self.form).filter(function (r) { return r.value === select.value; })[0];
        if (radio) { radio.checked = true; self.submitForm(); }
        else { var u = new URL(window.location.href); u.searchParams.set('sort_by', select.value); self.render(u.toString()); }
      });
    }
    submitForm() {
      var fd = new FormData(this.form);
      var params = new URLSearchParams();
      fd.forEach(function (v, k) { if (v !== '') params.append(k, v); });
      this.render(window.location.pathname + '?' + params.toString());
    }
    render(url, push) {
      var self = this;
      var u = new URL(url, window.location.origin);
      u.searchParams.delete('page');
      var fetchUrl = new URL(u.toString());
      fetchUrl.searchParams.set('section_id', this.sectionId);
      var wrap = this.querySelector('[data-results]');
      if (wrap) wrap.classList.add('is-loading');
      fetch(fetchUrl.toString()).then(function (r) { return r.text(); }).then(function (html) {
        var doc = parseHTML(html);
        ['[data-results]', '[data-active-filters]', '[data-product-count]', '[data-apply-count]', '[data-sort-select]'].forEach(function (sel) {
          var fresh = doc.querySelector('collection-facets ' + sel) || doc.querySelector(sel);
          $$(sel, self).forEach(function (el) { if (fresh) el.innerHTML = fresh.innerHTML; });
        });
        // refresh facet options (counts / disabled states) while keeping open panels & focus
        var body = self.form && self.form.querySelector('[data-facets-body]');
        var freshBody = doc.querySelector('[data-facets-body]');
        if (body && freshBody) {
          var open = $$('details[open]', body).map(function (d) { return d.id; });
          var focusedId = document.activeElement && document.activeElement.id;
          body.innerHTML = freshBody.innerHTML;
          open.forEach(function (id) { var d = document.getElementById(id); if (d) d.open = true; });
          if (focusedId) { var f = document.getElementById(focusedId); if (f) f.focus({ preventScroll: true }); }
        }
        if (push !== false) window.history.pushState({}, '', u.pathname + u.search);
      }).catch(function () { window.location.href = u.toString(); })
        .finally(function () { if (wrap) wrap.classList.remove('is-loading'); });
    }
  }
  customElements.define('collection-facets', CollectionFacets);

  /* Load more pagination ---------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-load-more]');
    if (!btn) return;
    e.preventDefault();
    var grid = document.getElementById(btn.getAttribute('data-grid'));
    if (!grid) return;
    btn.classList.add('is-loading');
    var url = new URL(btn.href, window.location.origin);
    url.searchParams.set('section_id', btn.getAttribute('data-section'));
    fetch(url.toString()).then(function (r) { return r.text(); }).then(function (html) {
      var doc = parseHTML(html);
      var freshGrid = doc.getElementById(grid.id);
      if (freshGrid) Array.prototype.slice.call(freshGrid.children).forEach(function (li) { grid.appendChild(li); });
      var freshPag = doc.querySelector('[data-pagination]');
      var pag = btn.closest('[data-pagination]');
      if (pag) { if (freshPag) pag.innerHTML = freshPag.innerHTML; else pag.remove(); }
    }).catch(function () { window.location.href = btn.href; });
  });

  /* Predictive search -------------------------------------------------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.defaultHTML = this.results ? this.results.innerHTML : '';
      var self = this;
      if (!this.input) return;
      this.input.addEventListener('input', utils.debounce(function () { self.search(self.input.value.trim()); }, 250));
      var dlg = this.closest('dialog');
      if (dlg) dlg.addEventListener('dialog:open', function () { setTimeout(function () { self.input.focus(); }, 60); });
    }
    search(q) {
      var self = this;
      if (!q) { this.results.innerHTML = this.defaultHTML; return; }
      if (this.controller) this.controller.abort();
      this.controller = 'AbortController' in window ? new AbortController() : null;
      var url = theme.routes.predictiveSearch + '?q=' + encodeURIComponent(q) +
        '&section_id=predictive-search&resources[type]=product,collection,query&resources[limit]=6&resources[options][unavailable_products]=last';
      fetch(url, this.controller ? { signal: this.controller.signal } : {}).then(function (r) { return r.text(); }).then(function (html) {
        var doc = parseHTML(html);
        var inner = doc.querySelector('[data-predictive-inner]');
        self.results.innerHTML = inner ? inner.innerHTML : '';
      }).catch(function () {});
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* Social visitor welcome --------------------------------------------- */
  function initSocialWelcome() {
    var strip = $('[data-social-welcome]');
    if (!strip || !theme.social || !theme.social.enabled) return;
    var key = 'ozara:social';
    var state = null;
    try { state = sessionStorage.getItem(key); } catch (e) { /* storage blocked */ }
    if (!state && utils.isSocialVisit(window.location.search, document.referrer, navigator.userAgent)) {
      state = 'show';
      try { sessionStorage.setItem(key, state); } catch (e) { /* noop */ }
    }
    if (state !== 'show') return;
    strip.hidden = false;
    var dismiss = function () {
      strip.hidden = true;
      try { sessionStorage.setItem(key, 'dismissed'); } catch (e) { /* noop */ }
    };
    var close = $('[data-social-dismiss]', strip);
    if (close) close.addEventListener('click', dismiss);
    var apply = $('[data-social-apply]', strip);
    if (apply) apply.addEventListener('click', function () {
      var codes = currentCodes();
      if (codes.indexOf(theme.social.code) === -1) codes.push(theme.social.code);
      applyDiscounts(codes, theme.social.code).then(function () { toast(apply.textContent.trim() + ' ✓'); dismiss(); });
    });
  }

  /* Boot --------------------------------------------------------------- */
  function boot() {
    initHeader();
    initSocialWelcome();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})(typeof window !== 'undefined' ? window : this);
